<template>
    <div>
        <h2>{{ step.title }}</h2>
        <p>If you need to leave this page, your answers will be saved for 24 hours.</p>
        <p class="step__notice">
            All fields with an asterisk are required
            <i class="fas fa-asterisk"></i>
        </p>
        <InputRadioGroupVar
            name="is_eligible_to_work"
            :required="checkIsRequired('is_eligible_to_work')"
            :error="formRef.errors.get('is_eligible_to_work')"
            v-model="formRef.is_eligible_to_work"
            :options="getField('is_eligible_to_work')['options']"
        >
            <template v-slot:question>
                {{ getField("is_eligible_to_work").question }}
            </template>
        </InputRadioGroupVar>

        <InputRadioGroupVar
            v-if="formRef.is_eligible_to_work == 'no'"
            name="ineligibility_reason"
            :required="checkIsRequired('ineligibility_reason')"
            :error="formRef.errors.get('ineligibility_reason')"
            v-model="formRef.ineligibility_reason"
            :options="getField('ineligibility_reason')['options']"
            @input="formRef.errors.clear('ineligibility_reason')"
        >
            <template v-slot:question>
                {{ getField("ineligibility_reason").question }}
            </template>
        </InputRadioGroupVar>

        <InputRadioGroupVar
            name="current_employment_situation"
            :required="checkIsRequired('current_employment_situation')"
            :error="formRef.errors.get('current_employment_situation')"
            v-model="formRef.current_employment_situation"
            :options="getField('current_employment_situation')['options']"
            @input="formRef.errors.clear('current_employment_situation')"
        >
            <template v-slot:question>
                {{ getField("current_employment_situation").question }}
            </template>
        </InputRadioGroupVar>

        <template v-if="formRef.current_employment_situation == 'working'">
            <InputNumber
                name="average_weekly_work_hours"
                :required="checkIsRequired('average_weekly_work_hours')"
                :error="formRef.errors.get('average_weekly_work_hours')"
                v-model="formRef.average_weekly_work_hours"
            >
                <template v-slot:question>
                    {{ getField("average_weekly_work_hours").question }}
                </template>
                <template v-slot:description>
                    {{ getField("average_weekly_work_hours").description }}
                </template>
            </InputNumber>

            <InputRadioGroup
                name="which_contract"
                :required="checkIsRequired('which_contract')"
                :error="formRef.errors.get('which_contract')"
                v-model="formRef.which_contract"
                :options="getField('which_contract')['options']"
                @input="formRef.errors.clear('which_contract')"
            >
                <template v-slot:question>
                    {{ getField("which_contract").question }}
                </template>
                <template v-slot:description>
                    {{ getField("which_contract").description }}
                </template>
            </InputRadioGroup>
        </template>

        <InputMonthsYears
            v-if="formRef.current_employment_situation == 'not_working'"
            name="how_long_since_last_worked"
            :error="
                formRef.errors.get('how_long_since_last_worked_years') ||
                formRef.errors.get('how_long_since_last_worked_months')
            "
            :years="formRef.how_long_since_last_worked_years"
            @years-updated="formRef.how_long_since_last_worked_years = $event,
            formRef.has_never_worked = false
            "
            :months="formRef.how_long_since_last_worked_months"
            @months-updated="formRef.how_long_since_last_worked_months = $event,
            formRef.has_never_worked = false
            "
        >
            <template v-slot:question>
                {{ getField("how_long_since_last_worked_months").question }}
            </template>
            <template v-slot:description>
                {{
                    getField("how_long_since_last_worked_months")
                        .description
                }}
            </template>

            <template v-slot:input>
                <div class="form-group">Or</div>
                <div class="form-group d-flex align-items-center">
                    <input
                        type="checkbox"
                        id="has_never_worked"
                        v-model="formRef.has_never_worked"
                        @change="clearfields()"
                    />
                    <label for="has_never_worked" class="m-0 ms-2 form-label">
                        I have never worked
                    </label>
                </div>
            </template>
        </InputMonthsYears>

        <InputRadioGroup
            v-if="formRef.current_employment_situation == 'not_working'"
            name="actively_looking_for_work"
            :required="checkIsRequired('actively_looking_for_work')"
            :error="formRef.errors.get('actively_looking_for_work')"
            v-model="formRef.actively_looking_for_work"
            :options="getField('actively_looking_for_work')['options']"
            @input="formRef.errors.clear('actively_looking_for_work')"
        >
            <template v-slot:question>
                {{ getField("actively_looking_for_work").question }}
            </template>
        </InputRadioGroup>

        <InputText
            v-if="formRef.current_employment_situation == 'unable_to_work'"
            name="why_unable_to_work"
            :required="checkIsRequired('why_unable_to_work')"
            :error="formRef.errors.get('why_unable_to_work')"
            v-model="formRef.why_unable_to_work"
            :is-multiline="true"
        >
            <template v-slot:question>
                {{ getField("why_unable_to_work").question }}
            </template>
            <template v-slot:description>
                {{ getField("why_unable_to_work").description }}
            </template>
        </InputText>

        <InputNumber
            v-if="formRef.current_employment_situation == 'volunteering'"
            name="volunteering_hours_per_week"
            :required="checkIsRequired('volunteering_hours_per_week')"
            :error="formRef.errors.get('volunteering_hours_per_week')"
            v-model="formRef.volunteering_hours_per_week"
        >
            <template v-slot:question>
                {{ getField("volunteering_hours_per_week").question }}
            </template>
            <template v-slot:description>
                {{ getField("volunteering_hours_per_week").description }}
            </template>
        </InputNumber>
    </div>
</template>

<script setup>
import InputText from "../../../Form/InputText";
import InputNumber from "../../../Form/InputNumber";
import InputRadioGroup from "../../../Form/InputRadioGroup";
import Form from "../../../../Form";
import { isRequired } from "./formHelper.js";
import InputMonthsYears from "../../../Form/InputMonthsYears";
import { ref, shallowRef, markRaw } from "vue";

const props = defineProps({
    step: Object,
    fields: Object,
    formData: Object
})

const InputRadioGroupVar = shallowRef(InputRadioGroup)
let formRef = markRaw(ref(new Form({
    is_eligible_to_work: null,
    ineligibility_reason: null,
    current_employment_situation: null,
    average_weekly_work_hours: null,
    which_contract: null,
    actively_looking_for_work: null,
    how_long_since_last_worked_years: null,
    how_long_since_last_worked_months: null,
    has_never_worked: false,
    why_unable_to_work: null,
    volunteering_hours_per_week: null,
}, props.formData)))

const getField = (fieldName) => {
    return props.fields[fieldName];
}

const checkIsRequired = (fieldName) => {
    var fieldData = getField(fieldName);
    return isRequired(fieldData, formRef.value);
}

function clearfields () {
    if (formRef.value.has_never_worked == true) {
        formRef.value.how_long_since_last_worked_years = null;
        formRef.value.how_long_since_last_worked_months = null;
    }
}

defineExpose({
  formRef
})
</script>
